/**
 * Browser format detection utilities
 * Detects which image formats the browser supports
 */

const isBrowser = typeof document !== 'undefined' && typeof Image !== 'undefined';

let cachedFormat: string | null = null;

/**
 * Detect the best image format supported by the browser
 * Priority: AVIF > WebP > JPEG
 * Uses synchronous detection for immediate results
 */
export function detectBestFormat(): string {
  if (cachedFormat !== null) {
    return cachedFormat;
  }

  if (!isBrowser) {
    // Server-side or non-browser environment
    cachedFormat = 'webp';
    return cachedFormat;
  }

  // Synchronous detection using canvas.toDataURL
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      cachedFormat = 'webp';
      return cachedFormat;
    }

    // Test AVIF
    try {
      const avifDataURL = canvas.toDataURL('image/avif');
      if (avifDataURL && avifDataURL.indexOf('data:image/avif') === 0) {
        cachedFormat = 'avif';
        return cachedFormat;
      }
    } catch (e) {
      // AVIF not supported
    }

    // Test WebP
    try {
      const webpDataURL = canvas.toDataURL('image/webp');
      if (webpDataURL && webpDataURL.indexOf('data:image/webp') === 0) {
        cachedFormat = 'webp';
        return cachedFormat;
      }
    } catch (e) {
      // WebP not supported
    }

    // Fallback to JPEG
    cachedFormat = 'jpg';
    return cachedFormat;
  } catch (e) {
    // Fallback to WebP if detection fails
    cachedFormat = 'webp';
    return cachedFormat;
  }
}


/**
 * Get the best format synchronously (uses cached value or detects)
 */
export function getBestFormatSync(): string {
  if (cachedFormat !== null) {
    return cachedFormat;
  }
  return detectBestFormat();
}

/**
 * Reset cached format (useful for testing)
 */
export function resetFormatCache(): void {
  cachedFormat = null;
}

